import React from 'react';

const TermsAndConditions: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
      <div className="bg-white shadow-lg rounded-xl overflow-hidden">
        <div className="px-4 sm:px-6 py-6 sm:py-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Terms and Conditions</h1>
          <p className="text-xs sm:text-sm text-gray-500 mb-6 sm:mb-8">Last updated: {new Date().toLocaleDateString()}</p>
          
          <div className="space-y-6 sm:space-y-8">
            <section>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 flex items-center">
                <span className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-t1blue text-white flex items-center justify-center mr-2 sm:mr-3 text-xs sm:text-sm">1</span>
                Introduction
              </h2>
              <div className="pl-8 sm:pl-11">
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  Welcome to T1 FX Team LLC. By accessing our platform and services, you agree to these terms and conditions. Please read them carefully before proceeding.
                </p>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed mt-3">
                  These terms govern your use of our automated trading services, website, and any related applications. If you do not agree with any part of these terms, please do not use our services.
                </p>
              </div>
            </section>
            
            <section>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 flex items-center">
                <span className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-t1blue text-white flex items-center justify-center mr-2 sm:mr-3 text-xs sm:text-sm">2</span>
                Definitions
              </h2>
              <div className="pl-8 sm:pl-11">
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3">
                  In these terms and conditions, the following definitions apply:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-gray-700">
                  <li><span className="font-medium">"Platform"</span> refers to the T1 FX Team LLC website and trading interface.</li>
                  <li><span className="font-medium">"Services"</span> refers to the automated trading services provided by T1 FX Team LLC.</li>
                  <li><span className="font-medium">"User"</span> refers to any individual or entity using our platform or services.</li>
                  <li><span className="font-medium">"Strategy"</span> refers to any trading strategy available on our platform.</li>
                  <li><span className="font-medium">"Subscription"</span> refers to the agreement between a user and T1 FX Team LLC for accessing our services.</li>
                </ul>
              </div>
            </section>
            
            <section>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 flex items-center">
                <span className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-t1blue text-white flex items-center justify-center mr-2 sm:mr-3 text-xs sm:text-sm">3</span>
                Account Registration
              </h2>
              <div className="pl-8 sm:pl-11">
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3">
                  To use our services, you must register for an account. By registering, you agree to:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-gray-700">
                  <li>Provide accurate and complete information about yourself.</li>
                  <li>Maintain the security of your account credentials.</li>
                  <li>Notify us immediately of any unauthorized access to your account.</li>
                  <li>Be at least 18 years old or the legal age in your jurisdiction.</li>
                  <li>Comply with all applicable laws and regulations.</li>
                </ul>
              </div>
            </section>
            
            <section>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 flex items-center">
                <span className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-t1blue text-white flex items-center justify-center mr-2 sm:mr-3 text-xs sm:text-sm">4</span>
                Trading Risks
              </h2>
              <div className="pl-8 sm:pl-11">
                <div className="bg-amber-50 border-l-4 border-amber-400 p-3 sm:p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-amber-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-xs sm:text-sm text-amber-700">
                        <span className="font-medium">Important:</span> Trading in financial markets carries significant risks. You should never invest money you cannot afford to lose.
                      </p>
                    </div>
                  </div>
                </div>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3">
                  By using our services, you acknowledge and accept the following risks:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-gray-700">
                  <li>Financial markets are volatile and can result in significant losses.</li>
                  <li>Past performance is not indicative of future results.</li>
                  <li>Automated trading systems may experience technical failures or delays.</li>
                  <li>Market conditions can change rapidly, affecting trading outcomes.</li>
                  <li>Leverage can amplify both gains and losses.</li>
                </ul>
              </div>
            </section>
            
            <section>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 flex items-center">
                <span className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-t1blue text-white flex items-center justify-center mr-2 sm:mr-3 text-xs sm:text-sm">5</span>
                Strategy Subscription
              </h2>
              <div className="pl-8 sm:pl-11">
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3">
                  When subscribing to a trading strategy, you agree to:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-gray-700">
                  <li>Follow the strategy's parameters and settings as specified.</li>
                  <li>Maintain sufficient funds in your trading account to cover potential losses.</li>
                  <li>Not attempt to modify or reverse-engineer the strategy's algorithms.</li>
                  <li>Accept that strategy performance may vary based on market conditions.</li>
                  <li>Comply with any specific terms associated with individual strategies.</li>
                </ul>
              </div>
            </section>
            
            <section>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 flex items-center">
                <span className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-t1blue text-white flex items-center justify-center mr-2 sm:mr-3 text-xs sm:text-sm">6</span>
                Fees and Payments
              </h2>
              <div className="pl-8 sm:pl-11">
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3">
                  Our fee structure is as follows:
                </p>
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-3">
                  <ul className="space-y-2 text-sm sm:text-base text-gray-700">
                    <li className="flex justify-between">
                      <span>Monthly subscription fee:</span>
                      <span className="font-medium">As per selected plan</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Payment processing fees:</span>
                      <span className="font-medium">Varies by payment method</span>
                    </li>
                  </ul>
                </div>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  All fees are non-refundable unless otherwise specified. We reserve the right to change our fee structure with 30 days' notice.
                </p>
              </div>
            </section>
            
            <section>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 flex items-center">
                <span className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-t1blue text-white flex items-center justify-center mr-2 sm:mr-3 text-xs sm:text-sm">7</span>
                Privacy and Data Protection
              </h2>
              <div className="pl-8 sm:pl-11">
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3">
                  We are committed to protecting your privacy and personal data. Our data collection and processing practices are outlined in our Privacy Policy.
                </p>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  By using our services, you consent to the collection, use, and storage of your personal information as described in our Privacy Policy.
                </p>
              </div>
            </section>
            
            <section>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 flex items-center">
                <span className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-t1blue text-white flex items-center justify-center mr-2 sm:mr-3 text-xs sm:text-sm">8</span>
                Termination
              </h2>
              <div className="pl-8 sm:pl-11">
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3">
                  We reserve the right to terminate or suspend your account and access to our services at our sole discretion, without notice, for conduct that we believe violates these terms or is harmful to other users, us, or third parties, or for any other reason.
                </p>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  Upon termination, your right to use the services will immediately cease. All provisions of the terms which by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
                </p>
              </div>
            </section>
            
            <section>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 flex items-center">
                <span className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-t1blue text-white flex items-center justify-center mr-2 sm:mr-3 text-xs sm:text-sm">9</span>
                Disclaimer
              </h2>
              <div className="pl-8 sm:pl-11">
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3">
                  The services are provided "as is" and "as available" without any warranties of any kind, either express or implied, including, but not limited to, the implied warranties of merchantability, fitness for a particular purpose, or non-infringement.
                </p>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  We do not warrant that the services will be uninterrupted, timely, secure, or error-free, that defects will be corrected, or that the services or the server that makes them available are free of viruses or other harmful components.
                </p>
              </div>
            </section>
            
            <section>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 flex items-center">
                <span className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-t1blue text-white flex items-center justify-center mr-2 sm:mr-3 text-xs sm:text-sm">10</span>
                Changes to Terms
              </h2>
              <div className="pl-8 sm:pl-11">
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  We reserve the right to modify these terms at any time. We will notify users of any material changes by posting the new terms on this page and updating the "Last updated" date. Your continued use of our services after such modifications constitutes your acknowledgment of the modified terms and your agreement to abide and be bound by them.
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions; 